import { useCallback, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { FreeCropCorner } from '../../types/freeCrop';
import { PREVIEW_MAX_DIMENSION } from '../../utils/freeCropUtils';
import { orderQuadCorners } from '../../utils/documentCornerUtils';

type UseFreeCropOpenCvParams = {
  cvLoaded: boolean;
  cv: any;
  baseCanvasRef: MutableRefObject<HTMLCanvasElement | null>;
  naturalSize: { width: number; height: number } | null;
  corners: FreeCropCorner[];
};

type UseFreeCropOpenCvResult = {
  createBaseMat: () => void;
  cleanupMats: () => void;
  applyPerspectiveTransform: (targetCanvas: HTMLCanvasElement, options: { quality: 'preview' | 'full' }) => void;
  detectDocumentCorners: () => FreeCropCorner[] | null;
};

const getDistance = (start: FreeCropCorner, end: FreeCropCorner) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
};

export const useFreeCropOpenCv = ({
  cvLoaded,
  cv,
  baseCanvasRef,
  naturalSize,
  corners,
}: UseFreeCropOpenCvParams): UseFreeCropOpenCvResult => {
  const baseMatRef = useRef<any>(null);
  const previewMatRef = useRef<any>(null);
  const previewSharesBaseRef = useRef(false);

  const cleanupMats = useCallback(() => {
    if (previewMatRef.current) {
      if (!previewSharesBaseRef.current) {
        previewMatRef.current.delete();
      }
      previewMatRef.current = null;
    }
    previewSharesBaseRef.current = false;
    if (baseMatRef.current) {
      baseMatRef.current.delete();
      baseMatRef.current = null;
    }
  }, []);

  const createBaseMat = useCallback(() => {
    if (!cvLoaded || !cv || !baseCanvasRef.current) {
      return;
    }
    cleanupMats();
    try {
      const fullMat = cv.imread(baseCanvasRef.current);
      baseMatRef.current = fullMat;
      const width = fullMat.cols;
      const height = fullMat.rows;
      const maxDim = Math.max(width, height);
      if (maxDim > PREVIEW_MAX_DIMENSION) {
        const scale = PREVIEW_MAX_DIMENSION / maxDim;
        const targetWidth = Math.max(1, Math.round(width * scale));
        const targetHeight = Math.max(1, Math.round(height * scale));
        const previewMat = new cv.Mat();
        cv.resize(fullMat, previewMat, new cv.Size(targetWidth, targetHeight), 0, 0, cv.INTER_AREA);
        previewMatRef.current = previewMat;
        previewSharesBaseRef.current = false;
      } else {
        previewMatRef.current = fullMat;
        previewSharesBaseRef.current = true;
      }
    } catch (error) {
      baseMatRef.current = null;
      previewMatRef.current = null;
      previewSharesBaseRef.current = false;
    }
  }, [baseCanvasRef, cleanupMats, cv, cvLoaded]);

  const applyPerspectiveTransform = useCallback(
    (targetCanvas: HTMLCanvasElement, { quality }: { quality: 'preview' | 'full' }) => {
      if (!cvLoaded || !cv || !naturalSize || corners.length !== 4) {
        return;
      }

      if (!baseMatRef.current) {
        createBaseMat();
      }
      const useFullResolution = quality === 'full';
      const sourceMat = useFullResolution ? baseMatRef.current : previewMatRef.current || baseMatRef.current;
      if (!sourceMat) {
        return;
      }

      const w = sourceMat.cols;
      const h = sourceMat.rows;
      if (!w || !h) {
        return;
      }
      const scaleX = w / naturalSize.width || 1;
      const scaleY = h / naturalSize.height || 1;

      const scaledCorners = corners.map((corner) => ({
        x: corner.x * scaleX,
        y: corner.y * scaleY,
      }));

      const outputWidth = Math.max(
        1,
        Math.round(
          Math.max(getDistance(scaledCorners[0], scaledCorners[1]), getDistance(scaledCorners[3], scaledCorners[2])),
        ),
      );
      const outputHeight = Math.max(
        1,
        Math.round(
          Math.max(getDistance(scaledCorners[0], scaledCorners[3]), getDistance(scaledCorners[1], scaledCorners[2])),
        ),
      );

      const destCorners = [
        { x: 0, y: 0 },
        { x: outputWidth, y: 0 },
        { x: outputWidth, y: outputHeight },
        { x: 0, y: outputHeight },
      ];

      const srcCoords = new Float32Array(scaledCorners.flatMap((point) => [point.x, point.y]));
      const dstCoords = new Float32Array(destCorners.flatMap((point) => [point.x, point.y]));

      const srcMat = cv.matFromArray(4, 1, cv.CV_32FC2, srcCoords);
      const dstMat = cv.matFromArray(4, 1, cv.CV_32FC2, dstCoords);
      const transformMatrix = cv.getPerspectiveTransform(srcMat, dstMat);

      const dst = new cv.Mat();
      cv.warpPerspective(
        sourceMat,
        dst,
        transformMatrix,
        new cv.Size(outputWidth, outputHeight),
        cv.INTER_LINEAR,
        cv.BORDER_CONSTANT,
        new cv.Scalar(255, 255, 255),
      );

      targetCanvas.width = outputWidth;
      targetCanvas.height = outputHeight;
      cv.imshow(targetCanvas, dst);
      dst.delete();

      srcMat.delete();
      dstMat.delete();
      transformMatrix.delete();
    },
    [corners, createBaseMat, cv, cvLoaded, naturalSize],
  );

  const detectDocumentCorners = useCallback((): FreeCropCorner[] | null => {
    if (!cvLoaded || !cv || !naturalSize) {
      return null;
    }

    if (!baseMatRef.current) {
      createBaseMat();
    }

    const sourceMat = previewMatRef.current || baseMatRef.current;
    if (!sourceMat) {
      return null;
    }

    const w = sourceMat.cols;
    const h = sourceMat.rows;
    if (!w || !h) {
      return null;
    }

    const imageArea = w * h;
    const minArea = imageArea * 0.06;
    const maxArea = imageArea * 0.98;

    const pickBestQuad = (contours: any) => {
      let best: { area: number; corners: FreeCropCorner[] } | null = null;

      for (let i = 0; i < contours.size(); i++) {
        const contour = contours.get(i);
        const approx = new cv.Mat();

        try {
          const peri = cv.arcLength(contour, true);
          cv.approxPolyDP(contour, approx, 0.02 * peri, true);

          if (approx.rows !== 4) {
            continue;
          }

          const area = Math.abs(cv.contourArea(approx));
          if (area < minArea) {
            continue;
          }
          if (area > maxArea) {
            continue;
          }

          if (typeof cv.isContourConvex === 'function' && !cv.isContourConvex(approx)) {
            continue;
          }

          const rect = cv.boundingRect(approx);
          const margin = 2;
          const touchesAllEdges =
            rect.x <= margin &&
            rect.y <= margin &&
            rect.x + rect.width >= w - margin &&
            rect.y + rect.height >= h - margin;
          if (touchesAllEdges) {
            continue;
          }

          const data32S: Int32Array | undefined = approx.data32S;
          const data32F: Float32Array | undefined = approx.data32F;
          const raw = data32S ?? data32F;
          if (!raw || raw.length < 8) {
            continue;
          }

          const points: FreeCropCorner[] = [];
          for (let j = 0; j < raw.length; j += 2) {
            points.push({ x: raw[j], y: raw[j + 1] });
          }

          const ordered = orderQuadCorners(points);
          if (!ordered) {
            continue;
          }

          if (!best || area > best.area) {
            best = { area, corners: ordered };
          }
        } finally {
          approx.delete();
          contour.delete();
        }
      }

      return best;
    };

    const gray = new cv.Mat();
    const normalized = new cv.Mat();
    const blurred = new cv.Mat();

    const edges = new cv.Mat();
    const edgeContours = new cv.MatVector();
    const edgeHierarchy = new cv.Mat();

    const rgb = new cv.Mat();
    const hsv = new cv.Mat();
    const hsvChannels = new cv.MatVector();
    const sMask = new cv.Mat();
    const vMask = new cv.Mat();
    const paperMask = new cv.Mat();
    const paperContours = new cv.MatVector();
    const paperHierarchy = new cv.Mat();

    const kernel3 = cv.Mat.ones(3, 3, cv.CV_8U);
    const kernel5 = cv.Mat.ones(5, 5, cv.CV_8U);
    const kernel9 = cv.Mat.ones(9, 9, cv.CV_8U);

    try {
      let normalizedReady = false;
      let rgbReady = false;

      try {
        cv.cvtColor(sourceMat, gray, cv.COLOR_RGBA2GRAY);
      } catch (error) {
        cv.cvtColor(sourceMat, gray, cv.COLOR_RGB2GRAY);
      }

      try {
        cv.equalizeHist(gray, normalized);
        normalizedReady = true;
      } catch (error) {
        normalizedReady = false;
      }

      const grayForEdges = normalizedReady ? normalized : gray;

      cv.GaussianBlur(grayForEdges, blurred, new cv.Size(5, 5), 0, 0, cv.BORDER_DEFAULT);
      cv.Canny(blurred, edges, 30, 120);
      cv.morphologyEx(edges, edges, cv.MORPH_CLOSE, kernel5);
      cv.dilate(edges, edges, kernel3);
      cv.findContours(edges, edgeContours, edgeHierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

      let best = pickBestQuad(edgeContours);

      try {
        cv.cvtColor(sourceMat, rgb, cv.COLOR_RGBA2RGB);
        rgbReady = true;
      } catch (error) {
        rgbReady = false;
      }

      try {
        if (rgbReady) {
          cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV);
        } else {
          cv.cvtColor(sourceMat, hsv, cv.COLOR_RGBA2HSV);
        }
        cv.split(hsv, hsvChannels);
        if (hsvChannels.size() < 3) {
          throw new Error('Invalid HSV channels');
        }
        const s = hsvChannels.get(1);
        const v = hsvChannels.get(2);

        cv.threshold(s, sMask, 70, 255, cv.THRESH_BINARY_INV);
        cv.threshold(v, vMask, 140, 255, cv.THRESH_BINARY);
        cv.bitwise_and(sMask, vMask, paperMask);

        cv.morphologyEx(paperMask, paperMask, cv.MORPH_CLOSE, kernel9);
        cv.morphologyEx(paperMask, paperMask, cv.MORPH_OPEN, kernel5);

        cv.findContours(paperMask, paperContours, paperHierarchy, cv.RETR_LIST, cv.CHAIN_APPROX_SIMPLE);

        const bestFromPaper = pickBestQuad(paperContours);
        if (bestFromPaper && (!best || bestFromPaper.area > best.area * 0.9)) {
          best = bestFromPaper;
        }

        s.delete();
        v.delete();
      } catch (error) {
        // Ignore HSV-based mask failures; edge-based detection can still succeed.
      }

      if (!best) {
        return null;
      }

      const scaleX = naturalSize.width / w;
      const scaleY = naturalSize.height / h;

      return best.corners.map((corner) => ({
        x: corner.x * scaleX,
        y: corner.y * scaleY,
      }));
    } catch (error) {
      return null;
    } finally {
      gray.delete();
      normalized.delete();
      blurred.delete();
      edges.delete();
      edgeContours.delete();
      edgeHierarchy.delete();

      rgb.delete();
      hsv.delete();
      hsvChannels.delete();
      sMask.delete();
      vMask.delete();
      paperMask.delete();
      paperContours.delete();
      paperHierarchy.delete();

      kernel3.delete();
      kernel5.delete();
      kernel9.delete();
    }
  }, [createBaseMat, cv, cvLoaded, naturalSize]);

  return {
    createBaseMat,
    cleanupMats,
    applyPerspectiveTransform,
    detectDocumentCorners,
  };
};
